import { ActiveConnection } from '../../connections/models/connectionTypes';
import { DatabaseObject } from '../models/databaseObjectTypes';
import {
  DatabaseExplorerNode,
  createConnectionNode,
  createDatabaseNode,
  createDatabaseObjectNode,
  createDatabaseObjectTypeFolders,
  ConnectionTreeNode,
  createSystemDatabasesFolderNode,
  isSystemDatabase
} from '../../explorer/models/DatabaseTreeModels';
import {
  ConnectionNodeBehaviors
} from '../../explorer/models/NodeBehaviors';
import {
  createDatabasesFolderNodeId,
  createConnectionNodeId,
  createTablesFolderNodeId,
  createViewsFolderNodeId,
  createStoredProceduresFolderNodeId,
  createFunctionsFolderNodeId,
  createPackagesFolderNodeId,
  createSequencesFolderNodeId,
  createSynonymsFolderNodeId,
  createDbLinksFolderNodeId,
  createMaterializedViewsFolderNodeId,
  createTypesFolderNodeId,
  createTableTypesFolderNodeId,
  createAliasTypesFolderNodeId,
  createClrTypesFolderNodeId,
  createDirectoriesFolderNodeId,
  createExternalTablesFolderNodeId,
  createPropertyGraphsFolderNodeId,
} from '../../explorer/utils/nodeIdUtils';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { isKustoProxyConnection } from '../../connections/services/kustoProxyConnection';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

function asDatabaseExplorerChildren(
  children: ConnectionTreeNode['children'] | DatabaseExplorerNode[] | undefined,
): DatabaseExplorerNode[] {
  return (children as DatabaseExplorerNode[] | undefined) ?? [];
}

function mergeConnectionChildren(
  defaultChildren: ConnectionTreeNode['children'] | DatabaseExplorerNode[] | undefined,
  existingChildren: ConnectionTreeNode['children'] | DatabaseExplorerNode[] | undefined,
): DatabaseExplorerNode[] {
  const nextDefaultChildren = asDatabaseExplorerChildren(defaultChildren);
  const nextExistingChildren = asDatabaseExplorerChildren(existingChildren);
  const existingById = new Map(nextExistingChildren.map((child) => [child.id, child]));
  const mergedChildren = nextDefaultChildren.map((defaultChild) =>
    mergeConnectionChild(defaultChild, existingById.get(defaultChild.id))
  );
  const seenIds = new Set(nextDefaultChildren.map((child) => child.id));

  nextExistingChildren.forEach((child) => {
    if (seenIds.has(child.id)) return;
    // F12 — pinned wrappers are STRUCTURAL: the pinned-objects store is the
    // sole source of truth for whether/where they appear under a connection.
    // If a pinned wrapper exists in the OLD tree but not in the freshly-built
    // defaults, that means the user unpinned it. Dropping it (instead of
    // pushing it to the bottom of the connection-children list, after the
    // regular `databases-folder` / security folders) keeps the rendered tree
    // in sync with the store.
    if (
      child.type === 'pinned-database' ||
      child.type === 'pinned-schema' ||
      child.type === 'pinned-folder' ||
      child.type === 'pinned-object' ||
      child.type === 'pinned-workspace' ||
      child.type === 'pinned-storage'
    ) {
      return;
    }
    mergedChildren.push(child);
  });

  return mergedChildren;
}

function mergeConnectionChild(
  defaultNode: DatabaseExplorerNode,
  existingNode?: DatabaseExplorerNode,
): DatabaseExplorerNode {
  if (!existingNode || existingNode.type !== defaultNode.type) {
    return defaultNode;
  }

  const merged: DatabaseExplorerNode = {
    ...defaultNode,
    children: mergeConnectionChildren(defaultNode.children, existingNode.children),
    isExpanded: existingNode.isExpanded ?? defaultNode.isExpanded,
    isLoading: existingNode.isLoading ?? defaultNode.isLoading,
  };

  // F7 — Preserve pin `resolution` across reconciles. `initializeDefaultStructure`
  // builds the fresh pinned wrappers WITHOUT a queryClient, so every default
  // node lands as `resolution: 'unresolved'`. The live cache pass (inside
  // `DatabaseExplorer.tsx`) upgrades that to `'resolved'` / `'stale'`; without
  // carrying it over here, every reconcile (e.g. a connection-status change)
  // visibly flickers each pinned row back to its unresolved style before the
  // subscriber re-stamps it.
  if (
    (defaultNode.type === 'pinned-database' ||
     defaultNode.type === 'pinned-schema' ||
     defaultNode.type === 'pinned-folder' ||
     defaultNode.type === 'pinned-object' ||
     defaultNode.type === 'pinned-workspace') &&
    'resolution' in existingNode &&
    typeof (existingNode as { resolution?: string }).resolution === 'string'
  ) {
    (merged as { resolution: string }).resolution =
      (existingNode as { resolution: string }).resolution;
  }

  return merged;
}

function reconcileConnectionNode(
  existingNode: ConnectionTreeNode,
  activeConnection: ActiveConnection,
): ConnectionTreeNode {
  const defaultNode = createNewConnectionNode(activeConnection);
  return {
    ...defaultNode,
    children: mergeConnectionChildren(defaultNode.children, existingNode.children),
    isExpanded: existingNode.isExpanded ?? defaultNode.isExpanded,
    isLoading: existingNode.isLoading ?? defaultNode.isLoading,
  };
}

/**
 * Builds a complete database tree from active connections.
 *
 * S23 note: this only builds the connection-derived part of the tree. The
 * conditional "Dashboards" root (a non-connection root, sibling to these
 * connection nodes) is layered in separately by a `useDashboardStore`
 * subscriber in `DatabaseExplorer.tsx` — it must survive independent of
 * connections coming and going, so it is NOT built here.
 */
export function buildDatabaseTree(
  activeConnections: ActiveConnection[]
): DatabaseExplorerNode[] {
  const treeNodes: DatabaseExplorerNode[] = [];

  // Create connection nodes with their default structure
  activeConnections.forEach(conn => {
    const connectionNode = createNewConnectionNode(conn);
    treeNodes.push(connectionNode);
  });

  return treeNodes;
}

/**
 * Creates a new connection node with expanded state
 */
function createNewConnectionNode(conn: ActiveConnection): ConnectionTreeNode {
  const engine = (conn.connection.engine || 'mssql') as DatabaseEngineId;

  const connectionNode = createConnectionNode(
    conn.id,
    safeConnectionName(conn.connection),
    {
      url: safeConnectionServer(conn.connection),
      username: conn.connection.login || '',
      driver: conn.connection.authentication || 'SQL Server authentication',
      port: conn.connection.port ? Number(conn.connection.port) : undefined,
      engine,
      status: conn.status,
      connectedAt: conn.connectedAt
    }
  );

  // Initialize the connection with its default structure (includes security folders based on engine).
  // `initializeDefaultStructure` sets databasesFolder.isExpanded = false when the connection has
  // ≥1 pin (spec §Connection lifecycle: suppress Databases auto-expand when pins exist).
  // The `createDatabasesFolderNode` default is `isExpanded: true`, so no override is needed when
  // there are no pins. Do NOT unconditionally force `true` here — that would clobber the suppression.
  connectionNode.children = ConnectionNodeBehaviors.initializeDefaultStructure(connectionNode, engine);

  // A closed connection never loads its databases (`DatabaseExplorer` skips
  // `loadDatabasesFolder` for a disconnected one), so leaving the folder
  // auto-expanded left it expanded-with-no-children — which `TreeNode` renders
  // as the loading skeleton, forever. Nothing is loading; collapse it so the
  // row reads as what it is. The reconnect path re-expands it (subject to the
  // same pin rule) before it loads.
  if (conn.status === 'disconnected') {
    collapseDatabasesFolder(connectionNode);
  }

  // Set connection as expanded by default
  connectionNode.isExpanded = true;

  return connectionNode;
}

/** The connection's Databases/Schemas folder, if the default structure built one. */
export function findDatabasesFolder(
  connectionNode: ConnectionTreeNode
): DatabaseExplorerNode | undefined {
  return (connectionNode.children as DatabaseExplorerNode[] | undefined)?.find(
    (child) => child.type === 'databases-folder'
  );
}

function collapseDatabasesFolder(connectionNode: ConnectionTreeNode): void {
  const databasesFolder = findDatabasesFolder(connectionNode);
  if (databasesFolder) {
    databasesFolder.isExpanded = false;
    databasesFolder.isLoading = false;
  }
}

/**
 * Is this Databases folder showing nothing but the skeleton?
 *
 * True when it has no children at all, or only the transient "Loading
 * databases…" placeholder — i.e. no listing ever landed. A folder in that state
 * that is also expanded pulses forever once its connection closes, because
 * nothing will ever come to fill it.
 */
export function isDatabasesFolderUnloaded(
  folder: { children?: ReadonlyArray<{ type: string; icon?: string }> } | undefined
): boolean {
  if (!folder) return false;
  const children = folder.children ?? [];
  return children.every((child) => child.type === 'message' && child.icon === 'loading');
}

/**
 * Merges active connections into existing tree, preserving state for existing connections
 * Returns the updated tree and a list of newly added connection IDs
 *
 * S23 note: `preservedNodes`/`reconciledNodes` below both `return true`/pass
 * through unchanged for any node whose `type !== 'connection'` — that already
 * covers the "Dashboards" root (and any future non-connection root) without
 * special-casing it here; no logic change was needed for it to survive a
 * connection add/remove reconcile.
 */
export function mergeConnectionsIntoTree(
  existingTree: DatabaseExplorerNode[],
  activeConnections: ActiveConnection[]
): { tree: DatabaseExplorerNode[]; newConnectionIds: string[] } {
  const activeConnectionIds = new Set(activeConnections.map(c => c.id));
  const existingConnectionIds = new Set(
    existingTree
      .filter(node => node.type === 'connection')
      .map(node => (node as ConnectionTreeNode).metadata.id)
  );

  // Find new connections (in active but not in existing tree)
  const newConnectionIds: string[] = [];
  activeConnections.forEach(conn => {
    if (!existingConnectionIds.has(conn.id)) {
      newConnectionIds.push(conn.id);
    }
  });

  // Keep existing nodes that are still active
  const preservedNodes = existingTree.filter(node => {
    if (node.type === 'connection') {
      return activeConnectionIds.has((node as ConnectionTreeNode).metadata.id);
    }
    return true;
  });
  const reconciledNodes = preservedNodes.map((node) => {
    if (node.type !== 'connection') {
      return node;
    }

    const connectionNode = node as ConnectionTreeNode;
    const activeConnection = activeConnections.find((conn) => conn.id === connectionNode.metadata.id);
    if (!activeConnection) {
      return node;
    }

    return reconcileConnectionNode(connectionNode, activeConnection);
  });

  // Add new connection nodes
  const newNodes = activeConnections
    .filter(conn => newConnectionIds.includes(conn.id))
    .map(conn => createNewConnectionNode(conn));

  return {
    tree: [...reconciledNodes, ...newNodes],
    newConnectionIds,
  };
}

/**
 * Adds databases to a connection node
 */
export function addDatabasesToTree(
  treeData: DatabaseExplorerNode[],
  connectionId: string,
  databaseNames: string[],
  engine?: DatabaseEngineId
): DatabaseExplorerNode[] {
  const resolveEngineFromTree = (): DatabaseEngineId | undefined => {
    if (engine) return engine;

    const connectionNodeId = createConnectionNodeId(connectionId);
    const node = findNodeInTree(connectionNodeId, treeData) as ConnectionTreeNode | undefined;
    const nodeEngine = node?.metadata?.engine;

    if (nodeEngine === 'mssql' || nodeEngine === 'pgsql' || nodeEngine === 'sqlite' || nodeEngine === 'mock') {
      return nodeEngine;
    }

    return undefined;
  };

  const resolvedEngine = resolveEngineFromTree();

  // Find the databases folder node
  const databasesFolderId = createDatabasesFolderNodeId(connectionId);
  const databasesFolderNode = findNodeInTree(databasesFolderId, treeData);

  if (!databasesFolderNode) {
    console.error(`Cannot find databases folder node with ID: ${databasesFolderId}`);
    return treeData;
  }

  // Separate system databases from user databases
  const systemDatabases = databaseNames.filter(dbName => isSystemDatabase(dbName));
  const userDatabases = databaseNames.filter(dbName => !isSystemDatabase(dbName));

  // Create database nodes for each category
  const systemDatabaseNodes = systemDatabases.map(dbName => {
    const dbNode = createDatabaseNode(connectionId, dbName, { engine: resolvedEngine });

    // Add standard object type folders under each database (with security folders if engine provided)
    dbNode.children = createDatabaseObjectTypeFolders(connectionId, dbName, resolvedEngine, isKustoProxyConnection(connectionId));

    return dbNode;
  });

  const userDatabaseNodes = userDatabases.map(dbName => {
    const dbNode = createDatabaseNode(connectionId, dbName, { engine: resolvedEngine });

    // Add standard object type folders under each database (with security folders if engine provided)
    dbNode.children = createDatabaseObjectTypeFolders(connectionId, dbName, resolvedEngine, isKustoProxyConnection(connectionId));

    return dbNode;
  });

  let allChildren: DatabaseExplorerNode[] = userDatabaseNodes;
  if (systemDatabaseNodes.length > 0) {
    const systemDatabasesFolderNode = createSystemDatabasesFolderNode(connectionId);
    systemDatabasesFolderNode.children = systemDatabaseNodes;
    allChildren = [systemDatabasesFolderNode, ...userDatabaseNodes];
  }

  // Update the databases folder node with the combined nodes
  return updateNodeInTree(treeData, databasesFolderId, {
    children: allChildren
  });
}

/** True when `next` is element-wise the very same nodes as `current`. */
export function isSameChildList(
  next: DatabaseExplorerNode[],
  current: DatabaseExplorerNode[],
): boolean {
  return next.length === current.length && next.every((node, i) => node === current[i]);
}

/**
 * Adds database objects to the tree
 */
export function addDatabaseObjectsToTree(
  treeData: DatabaseExplorerNode[],
  connectionId: string,
  database: string,
  objects: DatabaseObject[]
): DatabaseExplorerNode[] {
  // Group objects by type
  const objectsByType: Record<string, DatabaseObject[]> = {};
  
  objects.forEach(obj => {
    if (!objectsByType[obj.type]) {
      objectsByType[obj.type] = [];
    }
    objectsByType[obj.type].push(obj);
  });
  
  // Start with the unchanged tree
  let updatedTree = [...treeData];
  
  // For each object type, add the objects to the correct folder
  Object.entries(objectsByType).forEach(([type, typeObjects]) => {
    // Get the correct folder node ID format based on object type
    let folderNodeId;
    if (type === 'table') {
      folderNodeId = createTablesFolderNodeId(connectionId, database);
    } else if (type === 'view') {
      folderNodeId = createViewsFolderNodeId(connectionId, database);
    } else if (type === 'stored-procedure') {
      folderNodeId = createStoredProceduresFolderNodeId(connectionId, database);
    } else if (type === 'function') {
      folderNodeId = createFunctionsFolderNodeId(connectionId, database);
    } else if (type === 'package') {
      folderNodeId = createPackagesFolderNodeId(connectionId, database);
    } else if (type === 'sequence') {
      folderNodeId = createSequencesFolderNodeId(connectionId, database);
    } else if (type === 'synonym') {
      folderNodeId = createSynonymsFolderNodeId(connectionId, database);
    } else if (type === 'db-link') {
      folderNodeId = createDbLinksFolderNodeId(connectionId, database);
    } else if (type === 'materialized-view') {
      folderNodeId = createMaterializedViewsFolderNodeId(connectionId, database);
    } else if (type === 'type') {
      folderNodeId = createTypesFolderNodeId(connectionId, database);
    } else if (type === 'table-type') {
      folderNodeId = createTableTypesFolderNodeId(connectionId, database);
    } else if (type === 'alias-type') {
      folderNodeId = createAliasTypesFolderNodeId(connectionId, database);
    } else if (type === 'clr-type') {
      folderNodeId = createClrTypesFolderNodeId(connectionId, database);
    } else if (type === 'directory') {
      folderNodeId = createDirectoriesFolderNodeId(connectionId, database);
    } else if (type === 'external-table') {
      folderNodeId = createExternalTablesFolderNodeId(connectionId, database);
    } else if (type === 'property-graph') {
      folderNodeId = createPropertyGraphsFolderNodeId(connectionId, database);
    } else {
      console.error(`Unknown object type: ${type}`);
      return;
    }
    
    // Find folder node by its expected ID
    const folderNode = findNodeInTree(folderNodeId, updatedTree);
    
    if (folderNode) {
      // This runs on the background preload path, so the folder may already be
      // expanded with object nodes the user has drilled into. Reuse the existing
      // node for every object that is still present: replacing it with a fresh
      // one would collapse it and discard its loaded children (columns, indexes).
      const existing = (folderNode.children ?? []) as DatabaseExplorerNode[];
      const existingById = new Map(existing.map((node) => [node.id, node]));

      const objectNodes = typeObjects.map(obj => {
        const fresh = createDatabaseObjectNode(obj, folderNodeId);
        return existingById.get(fresh.id) ?? fresh;
      });

      // Unchanged listing (the common case): skip the write so React doesn't
      // remount the subtree and swallow an in-flight expand click.
      if (isSameChildList(objectNodes, existing)) return;

      updatedTree = updateNodeInTree(updatedTree, folderNodeId, {
        children: objectNodes
      });
    } else {
      console.error(`Could not find folder node with ID: ${folderNodeId} for ${type}s in database ${database}`);
    }
  });
  
  return updatedTree;
}

/**
 * Finds a node in the tree by its ID
 */
function findNodeInTree(
  nodeId: string, 
  nodes: DatabaseExplorerNode[]
): DatabaseExplorerNode | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(nodeId, node.children as DatabaseExplorerNode[]);
      if (found) {
        return found;
      }
    }
  }
  
  return undefined;
}

/**
 * Updates a node in the tree
 */
function updateNodeInTree<T extends DatabaseExplorerNode>(
  nodes: T[],
  nodeId: string,
  updates: Partial<DatabaseExplorerNode>
): T[] {
  return nodes.map(node => {
    if (node.id === nodeId) {
      return { ...node, ...updates } as T;
    }
    
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(
          node.children as T[],
          nodeId,
          updates
        )
      } as T;
    }
    
    return node;
  });
} 
